<script setup lang="ts">
import {ref, unref, toRefs, computed, provide, watchEffect, markRaw, effectScope, onUnmounted,} from 'vue';
import vtkInteractorStyleManipulator from '@kitware/vtk.js/Interaction/Style/InteractorStyleManipulator';
import vtkMouseCameraTrackballPanManipulator from '@kitware/vtk.js/Interaction/Manipulators/MouseCameraTrackballPanManipulator';
import vtkMouseCameraTrackballZoomManipulator from '@kitware/vtk.js/Interaction/Manipulators/MouseCameraTrackballZoomManipulator';
import { useVtkView } from '@/src/core/vtk/useVtkView';
import { useImage } from '@/src/composables/useCurrentImage';
import { useVtkInteractorStyle } from '@/src/core/vtk/useVtkInteractorStyle';
import { useVtkInteractionManipulator } from '@/src/core/vtk/useVtkInteractionManipulator';
import { LPSAxisDir } from '@/src/types/lps';
import { watchImmediate } from '@vueuse/core';
// import { resetCameraToImage } from '@/src/utils/camera';
import { usePersistCameraConfig } from '@/src/composables/usePersistCameraConfig';
import { Maybe } from '@/src/types';
import { VtkViewApi } from '@/src/types/vtk-types';
import { VtkViewContext } from '@/src/components/vtk/context';
import vtkMouseCameraTrackballRotateManipulator from '@kitware/vtk.js/Interaction/Manipulators/MouseCameraTrackballRotateManipulator';
import vtkBoundingBox from '@kitware/vtk.js/Common/DataModel/BoundingBox';

import type { Vector3 } from '@kitware/vtk.js/types';

import { useViewStore } from '@/src/store/views';
import useViewSliceStore from '@/src/store/view-configs/slicing';
import { useSliceConfig } from '@/src/composables/useSliceConfig';
import { useImageStore } from '@/src/store/datasets-images';
import useResliceCursorStore from '@/src/store/reslice-cursor';
import { vec3 } from 'gl-matrix';
import { vtkFieldRef } from '@/src/core/vtk/vtkFieldRef';
import { getLPSAxisFromDir } from '@/src/utils/lps';



interface Props {
  viewId: string;
  imageId: Maybe<string>;
  viewDirection: LPSAxisDir;
  viewUp: LPSAxisDir;
}

const props = defineProps<Props>();
const {
  viewId: viewID,
  imageId: imageID,
  viewDirection,
  viewUp
} = toRefs(props);

const vtkContainerRef = ref<HTMLElement>();

const { imageData, metadata: imageMetadata } = useImage(imageID);


// base image
const imageStore = useImageStore();
const { slice: currentSlice, range: sliceRange } = useSliceConfig( viewID, imageID);

// reslice cursor
const resliceStore = useResliceCursorStore();
const { resliceCursor, resliceCursorState } = resliceStore;

const planeOrigin = vtkFieldRef(resliceCursorState, {
  get: () => resliceCursorState.getCenter(),
  set: (v) => resliceCursor.setCenter(v),
});


const viewSliceStore = useViewSliceStore();
const viewStore = useViewStore();

// only gets views that have a slicing config
const currentViewIDs = computed(() => {
  if (unref(imageID)) {
    return viewStore.viewIDs.filter(
      (viewID) => !!viewSliceStore.getConfig(viewID, unref(imageID))
    );
  }
  return [];
});

// the core update camera function
const updateResliceCamera = (updateImgOrigin: boolean) => {

  // if (!vtkView.value || !resliceCursorState.getImage()) return;

  const origin: vec3 = [0, 0, 0];
  currentViewIDs.value.forEach((viewID) => {
    const { slice: thisViewSlice } = useSliceConfig(viewID,imageID);
    const thisSliceConfig = viewSliceStore.getConfig(viewID, unref(imageID));
    const thisAxis = unref(imageMetadata).lpsOrientation[getLPSAxisFromDir(thisSliceConfig!.axisDirection)];
    origin[thisAxis] = thisViewSlice.value;
  });

  const origin_world = vec3.create();
  vec3.transformMat4(origin_world, origin, unref(imageMetadata).indexToWorld);
  if( updateImgOrigin )
  {
    const tmp = vec3.create();
    vec3.transformMat4(tmp, planeOrigin.value, unref(imageMetadata).worldToIndex);

    const delta = vec3.create();
    delta[unref(imageMetadata).mainAxis] = Math.floor(tmp[unref(imageMetadata).mainAxis]) - origin[unref(imageMetadata).mainAxis]
    // console.log("delta", delta)

    vec3.transformMat4(delta, delta, unref(imageMetadata).indexToWorld);
    // console.log("meaning delta world", delta)

    imageData.value!.setOrigin(delta as Vector3)

    if(imageID.value)
      imageStore.updateMetaData(imageID.value)

    if(origin[unref(imageMetadata).mainAxis] == 0)
      origin[unref(imageMetadata).mainAxis] += 0.001

      vec3.transformMat4(origin_world, origin, unref(imageMetadata).indexToWorld);
    }
  planeOrigin.value = origin_world as Vector3
};


console.log("SALUT LES MECS !")

watchImmediate(
  currentSlice,
  () => {
    console.log("bon point, currentSlice is changing", currentSlice.value)
    const sliceConfig = viewSliceStore.getConfig(viewID.value, unref(imageID));
    if(!sliceConfig)
      return;
    const axis = unref(imageMetadata).lpsOrientation[getLPSAxisFromDir(sliceConfig!.axisDirection)];

    if(unref(imageMetadata).isDynamic && axis == unref(imageMetadata).mainAxis)
      updateResliceCamera(true);
    else
      updateResliceCamera(false);
});


</script>

<template>
  <div ref="vtkContainerRef"><slot></slot></div>
</template>
